import React, { useState, useRef, useMemo, useEffect } from 'react'
import { mysiteContainer } from 'src/models/Mysite'
import {
  DesktopIcon,
  TabletIcon,
  MobileIcon,
  CloseIcon,
} from './MysitePreviewHeaderIcons'
import { Loader } from 'src/views/components/Loader'
import { IS_BROWSER } from 'config/appConfig'
import { getLocaleCodeFromUrl, i18nTranslate } from 'src/utils'
import './styles.scss'

const DEVICE_TYPES = {
  DESKTOP: 'desktop',
  TABLET: 'tablet',
  MOBILE: 'mobile',
}

const PREVIEW_URL_PARAMS = {
  PREVIEW: 'preview',
  IN_IFRAME: 'inIframe',
}

const nameSpace = 'ssr-resource'

const MysitePreviewHeader = () => {
  const isInsideIframe = new URLSearchParams(window?.location?.search).has(
    PREVIEW_URL_PARAMS.IN_IFRAME
  )

  if (isInsideIframe) {
    return null
  }

  const detectDeviceType = () => {
    if (!IS_BROWSER) return DEVICE_TYPES.DESKTOP

    const screenWidth = window?.innerWidth
    const userAgent = navigator?.userAgent?.toLowerCase()
    const isMobileUserAgent =
      /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(
        userAgent
      )

    if (screenWidth <= 768 || isMobileUserAgent) {
      return DEVICE_TYPES.MOBILE
    } else if (screenWidth <= 1024) {
      return DEVICE_TYPES.TABLET
    }
    return DEVICE_TYPES.DESKTOP
  }

  const [selectedDevice, setSelectedDevice] = useState(detectDeviceType)
  const [isLoading, setIsLoading] = useState(true)

  const previewIframeRef = useRef(null)
  const previewMaskRef = useRef(null)

  const currentLocale = getLocaleCodeFromUrl({
    defaultLocale: 'en_US',
    isReverseType: true,
  })

  const shopDisplayName = useMemo(() => {
    const mysiteResponse = mysiteContainer?.mysiteShopHeaderResponse

    if (!mysiteResponse || !currentLocale) return ''

    const { user, market } = mysiteResponse || {}
    const currentRegion = currentLocale?.split('_')?.[0] ?? 'en'
    const localizedShopDetails = market?.lang?.[currentRegion]
    const shopNameMarket = localizedShopDetails?.storeName
    const shopName = user?.shopName

    return shopName || shopNameMarket?.trim() || ''
  }, [currentLocale, mysiteContainer?.mysiteShopHeaderResponse])

  const generateIframeUrl = () => {
    if (IS_BROWSER) {
      const currentUrl = new URL(
        window?.location?.origin + window?.location?.pathname
      )
      currentUrl.searchParams.set(PREVIEW_URL_PARAMS.IN_IFRAME, 'true')
      currentUrl.searchParams.set('preview', 'true')

      return currentUrl.toString()
    }
  }

  const handleDeviceSelection = deviceType => {
    setSelectedDevice(deviceType)
  }

  const handleIframeLoad = () => {
    setTimeout(() => setIsLoading(false), 2000)
  }

  const deviceConfig = [
    { type: DEVICE_TYPES.DESKTOP, Icon: DesktopIcon, title: 'Desktop View' },
    { type: DEVICE_TYPES.TABLET, Icon: TabletIcon, title: 'Tablet View' },
    { type: DEVICE_TYPES.MOBILE, Icon: MobileIcon, title: 'Mobile View' },
  ]

  const renderDeviceButton = (deviceType, Icon, title) => (
    <button
      key={deviceType}
      className={`mysite-header-preview__device mysite-header-preview__device--${deviceType} ${
        selectedDevice === deviceType ? 'active' : ''
      }`}
      onClick={() => handleDeviceSelection(deviceType)}
      title={title}
      type="button">
      <Icon className="mysite-device-icon" />
    </button>
  )

  const handleMaskScroll = event => {
    const iframeElement = previewIframeRef.current
    if (!iframeElement) return

    event?.preventDefault()

    try {
      const iframeWindow = iframeElement?.contentWindow
      if (iframeWindow) {
        iframeWindow?.scrollBy(event?.deltaX || 0, event?.deltaY || 0)
      }
    } catch (error) {
      console.warn(
        'Cross-origin iframe scroll access restricted:',
        error?.message
      )
    }
  }

  // Detect device type on component mount
  useEffect(() => {
    if (IS_BROWSER) {
      const initialDeviceType = detectDeviceType()
      setSelectedDevice(initialDeviceType)
      document.body.classList.add('mysite-preview')
      document.body.classList.add('no-scroll')
    }
  }, [])

  useEffect(() => {
    const maskElement = previewMaskRef.current
    if (!maskElement) return

    maskElement.addEventListener('wheel', handleMaskScroll, { passive: false })
    return () => {
      maskElement.removeEventListener('wheel', handleMaskScroll)
    }
  }, [])

  return (
    <div className="mysite-preview-wrapper">
      <div className="mysite-header-preview">
        <div className="mysite-header-preview__container">
          <div className="mysite-header-preview__content">
            {shopDisplayName && (
              <span className="mysite-header-preview__title">
                {shopDisplayName} -{' '}
                {i18nTranslate('mysite.preview', 'Preview', {
                  nameSpace: nameSpace,
                })}
              </span>
            )}
          </div>

          <div className="mysite-header-preview__devices">
            {deviceConfig.map(({ type, Icon, title }) =>
              renderDeviceButton(type, Icon, title)
            )}
          </div>

          <button
            className="mysite-header-preview__close"
            onClick={() => IS_BROWSER && window?.close()}
            aria-label="Close preview"
            type="button">
            <CloseIcon className="mysite-close-icon" />
            <span className="mysite-header-preview__close-text">
              {i18nTranslate('mysite.closeWindow', 'Close Window', {
                nameSpace: nameSpace,
              })}
            </span>
          </button>
        </div>
      </div>

      <div className={`mysite-preview-container ${selectedDevice}-view`}>
        {isLoading && (
          <div className="mysite-preview-loading-overlay">
            <Loader />
          </div>
        )}
        <iframe
          ref={previewIframeRef}
          src={generateIframeUrl()}
          className={`mysite-preview-iframe mysite-preview-iframe--${selectedDevice}`}
          title={`${shopDisplayName || 'Mysite'} Preview - ${selectedDevice} view`}
          frameBorder="0"
          sandbox="allow-same-origin allow-scripts"
          onLoad={handleIframeLoad}
        />
        <div className="mysite-preview-mask" ref={previewMaskRef} />
      </div>
    </div>
  )
}

export default MysitePreviewHeader
